import React, { useState, useEffect, useRef } from 'react';

interface UpgradeBenefitsPageProps {
    currentTier?: string;
    onUpgrade?: (tier: 'business' | 'agency') => void;
    onNavigate?: (nav: { page: string; params?: any }) => void;
    onBack?: () => void;
}

const CheckIcon = () => (
    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="8" fill="currentColor" fillOpacity="0.15" />
        <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const StarIcon = () => (
    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 1l1.85 3.75L14 5.5l-3 2.92.71 4.13L8 10.5l-3.71 1.95L5 8.42 2 5.5l4.15-.75L8 1z" />
    </svg>
);

const BUSINESS_FEATURES = [
    { label: 'Executive Protection (EP) Suite', detail: 'Full mission planning, threat analysis & OPORD generation' },
    { label: 'Crisis Management Plans (CMP)', detail: 'Insights generated crisis response frameworks & playbooks' },
    { label: 'HSSE Compliance Suite', detail: 'Health, safety, security & environmental risk audits' },
    { label: 'VCMC Simulation Platform', detail: 'Live crisis scenario exercises with AI opposing force' },
    { label: 'Learning Management System', detail: 'Custom courses, competency builders & certification tracking' },
    { label: 'SRM Command Center (Full)', detail: 'Project blueprints, module tracking & team coordination' },
    { label: 'Advanced Report Generation', detail: 'Executive briefs, PMESII-PT & composite intelligence reports' },
    { label: 'Up to 5 Admin Seats', detail: 'Expand your team with role-based access control' },
    { label: 'Priority Support', detail: 'Dedicated response within 24 hours' },
];

const AGENCY_FEATURES = [
    { label: 'Everything in Business', detail: 'Full access to all Business Intelligence features' },
    { label: 'Multi-Client Workspace Management', detail: 'Create, manage & activate up to 10 client programs' },
    { label: 'Client Portal Access', detail: 'Branded portals for clients to view reports & interact' },
    { label: 'White-Label Report Branding', detail: 'Brand all deliverables with your agency logo & identity' },
    { label: 'Agency Billing Infrastructure', detail: 'Manage client subscriptions & add-on billing from one dashboard' },
    { label: 'Unlimited Intelligence Drafting', detail: 'No token caps on report & analysis generation' },
    { label: 'Priority Pipeline Processing', detail: 'Fast-lane AI processing for high-volume agency ops' },
    { label: 'Dedicated Agency Dashboard', detail: 'Real-time overview of all client programs & activity' },
    { label: 'Revenue Sharing Framework', detail: 'Bill clients for managed services — keep the margin' },
];

const COMPARISON = [
    { feature: 'Global Risk Map & Daily Feed', individual: true, business: true, agency: true },
    { feature: 'SRM Intelligence Core', individual: true, business: true, agency: true },
    { feature: 'Standard Report Generation', individual: true, business: true, agency: true },
    { feature: 'SRM Architect & Command Center', individual: 'Limited', business: true, agency: true },
    { feature: 'Executive Protection Suite', individual: false, business: true, agency: true },
    { feature: 'Crisis Management Plans', individual: false, business: true, agency: true },
    { feature: 'HSSE Compliance Suite', individual: false, business: true, agency: true },
    { feature: 'VCMC Simulation', individual: false, business: true, agency: true },
    { feature: 'Learning Management System', individual: false, business: true, agency: true },
    { feature: 'Team Admin Seats', individual: '1 seat', business: '5 seats', agency: 'Unlimited' },
    { feature: 'Multi-Client Workspaces', individual: false, business: false, agency: true },
    { feature: 'Client Portal & White-Labelling', individual: false, business: false, agency: true },
    { feature: 'Agency Billing Tools', individual: false, business: false, agency: true },
];

function FeatureCheck({ value }: { value: boolean | string }) {
    if (value === true) return <span className="text-emerald-400"><svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg></span>;
    if (value === false) return <span className="text-slate-600"><svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg></span>;
    return <span className="text-xs text-blue-400 font-semibold text-center block">{value}</span>;
}

function AnimatedCounter({ to, suffix = '' }: { to: number; suffix?: string }) {
    const [count, setCount] = useState(0);
    const ref = useRef<HTMLSpanElement>(null);
    const started = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && !started.current) {
                started.current = true;
                let start = 0;
                const step = to / 40;
                const timer = setInterval(() => {
                    start += step;
                    if (start >= to) { setCount(to); clearInterval(timer); }
                    else setCount(Math.floor(start));
                }, 30);
            }
        });
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [to]);

    return <span ref={ref}>{count}{suffix}</span>;
}

const UpgradeBenefitsPage: React.FC<UpgradeBenefitsPageProps> = ({ currentTier, onUpgrade, onNavigate, onBack }) => {
    const [activeTab, setActiveTab] = useState<'business' | 'agency'>('business');
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

    const prices = {
        business: { monthly: 349, annual: 279 },
        agency: { monthly: 649, annual: 519 },
    };

    const handleUpgrade = (tier: 'business' | 'agency') => {
        if (onUpgrade) {
            onUpgrade(tier);
        } else if (onNavigate) {
            onNavigate({ page: 'subscriptionSetup', params: { preselectedPlan: tier } });
        }
    };

    return (
        <div className="min-h-screen bg-[#080d1a] text-white overflow-x-hidden">
            {/* NAV BACK */}
            {onBack && (
                <div className="sticky top-0 z-50 bg-[#080d1a]/80 backdrop-blur-md border-b border-white/5 px-6 py-3">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors font-medium"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back
                    </button>
                </div>
            )}

            {/* ═══ HERO ═══ */}
            <div className="relative pt-20 pb-16 px-6 text-center overflow-hidden">
                {/* Background radial glows */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600/10 blur-[120px] rounded-full" />
                    <div className="absolute top-20 left-1/4 w-[300px] h-[300px] bg-violet-600/8 blur-[100px] rounded-full" />
                    <div className="absolute top-20 right-1/4 w-[300px] h-[300px] bg-amber-500/6 blur-[100px] rounded-full" />
                </div>

                <div className="relative z-10 max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold tracking-widest uppercase mb-6">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        Upgrade Your Intelligence Capability
                    </div>

                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 leading-[1.05]">
                        Unlock the Full{' '}
                        <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-cyan-400 bg-clip-text text-transparent">
                            Intelligence Suite
                        </span>
                    </h1>

                    <p className="text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto mb-10">
                        From mission-critical EP operations to multi-client agency management — 
                        IntelOptics scales with your organisation's ambition. 
                        Choose the tier that matches your mandate.
                    </p>

                    {/* Stats bar */}
                    <div className="grid grid-cols-3 gap-6 max-w-xl mx-auto mb-10">
                        {[
                            { value: 199, suffix: '+', label: 'Countries Tracked' },
                            { value: 40, suffix: '+', label: 'AI-Powered Modules' },
                            { value: 99, suffix: '.9%', label: 'Uptime SLA' },
                        ].map(stat => (
                            <div key={stat.label} className="text-center">
                                <div className="text-2xl font-black text-white">
                                    <AnimatedCounter to={stat.value} suffix={stat.suffix} />
                                </div>
                                <div className="text-xs text-slate-500 mt-1 font-medium">{stat.label}</div>
                            </div>
                        ))}
                    </div>

                    {/* Billing toggle */}
                    <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-full px-2 py-2">
                        <button
                            onClick={() => setBillingCycle('monthly')}
                            className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all ${billingCycle === 'monthly' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setBillingCycle('annual')}
                            className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${billingCycle === 'annual' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}
                        >
                            Annual
                            <span className="bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">SAVE 20%</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* ═══ TIER CARDS ═══ */}
            <div className="max-w-5xl mx-auto px-6 pb-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* ── BUSINESS CARD ── */}
                    <div className="relative group">
                        <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-blue-500 via-violet-500 to-blue-600 opacity-60 group-hover:opacity-100 transition-opacity duration-300 blur-[1px]" />
                        <div className="relative rounded-2xl bg-[#0d1528] border border-white/5 p-8 h-full flex flex-col">
                            {/* Header */}
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-500/25 text-blue-400 text-xs font-bold tracking-widest uppercase mb-3">
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" /></svg>
                                        Business Intelligence
                                    </div>
                                    <h2 className="text-2xl font-black text-white">Business</h2>
                                    <p className="text-slate-400 text-sm mt-1">For security teams & corporate risk managers</p>
                                </div>
                                <div className="text-right">
                                    <div className="text-3xl font-black text-white">
                                        ${prices.business[billingCycle]}
                                        <span className="text-base font-normal text-slate-400">/mo</span>
                                    </div>
                                    {billingCycle === 'annual' && (
                                        <div className="text-xs text-emerald-400 font-semibold mt-1">
                                            Billed ${prices.business.annual * 12}/yr
                                        </div>
                                    )}
                                    <div className="text-[10px] text-slate-500 mt-0.5">exc. taxes</div>
                                </div>
                            </div>

                            {/* Features */}
                            <div className="space-y-3 flex-1 mb-8">
                                {BUSINESS_FEATURES.map(f => (
                                    <div key={f.label} className="flex items-start gap-3 text-blue-400">
                                        <CheckIcon />
                                        <div>
                                            <div className="text-sm font-semibold text-white">{f.label}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">{f.detail}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* CTA */}
                            <button
                                onClick={() => handleUpgrade('business')}
                                className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white font-bold text-sm tracking-wide transition-all shadow-lg shadow-blue-900/40 hover:shadow-blue-800/60 hover:-translate-y-0.5"
                            >
                                {currentTier === 'business' ? '✓ Your Current Plan' : 'Upgrade to Business →'}
                            </button>
                            {currentTier !== 'business' && (
                                <p className="text-center text-xs text-slate-500 mt-3">No contracts · Cancel anytime · Instant access</p>
                            )}
                        </div>
                    </div>

                    {/* ── AGENCY CARD ── */}
                    <div className="relative group">
                        <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-amber-600 opacity-60 group-hover:opacity-100 transition-opacity duration-300 blur-[1px]" />
                        <div className="relative rounded-2xl bg-[#0d1528] border border-white/5 p-8 h-full flex flex-col">
                            {/* Most Popular badge */}
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                <div className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[11px] font-black tracking-widest uppercase shadow-lg shadow-amber-900/40">
                                    <StarIcon />
                                    Maximum Revenue Potential
                                </div>
                            </div>

                            {/* Header */}
                            <div className="flex items-start justify-between mb-6 mt-3">
                                <div>
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/25 text-amber-400 text-xs font-bold tracking-widest uppercase mb-3">
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" /></svg>
                                        Agency Command
                                    </div>
                                    <h2 className="text-2xl font-black text-white">Agency</h2>
                                    <p className="text-slate-400 text-sm mt-1">For security consultancies managing multiple clients</p>
                                </div>
                                {/* Price replaced with Coming Soon */}
                                <div className="text-right">
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-400 text-xs font-black uppercase tracking-widest">
                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                                        Coming Soon
                                    </div>
                                    <div className="text-[10px] text-slate-600 mt-1.5">Future release</div>
                                </div>
                            </div>

                            {/* Features */}
                            <div className="space-y-3 flex-1 mb-8">
                                {AGENCY_FEATURES.map(f => (
                                    <div key={f.label} className="flex items-start gap-3 text-amber-400">
                                        <CheckIcon />
                                        <div>
                                            <div className="text-sm font-semibold text-white">{f.label}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">{f.detail}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* CTA — Coming Soon */}
                            <div className="w-full py-4 rounded-xl border-2 border-dashed border-white/10 bg-white/[0.02] text-center">
                                <p className="text-sm font-bold text-slate-400">
                                    📢 Look out for the future release of our Agency Business Model — coming soon.
                                </p>
                            </div>
                            <p className="text-center text-xs text-slate-600 mt-3">Notify me at <a href="mailto:contact@eprims.com" className="text-amber-400/60 hover:text-amber-400 underline transition-colors">contact@eprims.com</a></p>
                        </div>
                    </div>
                </div>

                {/* ═══ WHAT YOU UNLOCK ═══ */}
                <div className="mt-20">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-black text-white mb-3">What You Unlock</h2>
                        <p className="text-slate-400 max-w-lg mx-auto">Explore the exclusive suites available to Business and Agency tiers</p>
                    </div>

                    {/* Tab switcher */}
                    <div className="flex justify-center mb-10">
                        <div className="inline-flex bg-white/5 border border-white/10 rounded-xl p-1 gap-1">
                            {(['business', 'agency'] as const).map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-6 py-2.5 rounded-lg text-sm font-bold capitalize transition-all ${
                                        activeTab === tab
                                            ? tab === 'business'
                                                ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-lg'
                                                : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                                            : 'text-slate-400 hover:text-white'
                                    }`}
                                >
                                    {tab === 'business' ? '🏢 Business' : '🏛️ Agency'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Feature showcase cards */}
                    {activeTab === 'business' ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {[
                                {
                                    icon: '🛡️',
                                    title: 'Executive Protection Suite',
                                    desc: 'End-to-end EP mission planning with AI-driven threat analysis, advance work coordination, and full OPORD generation for complex protective operations across any theatre.',
                                    color: 'from-blue-500/10 to-violet-500/10 border-blue-500/20',
                                    tag: 'Mission Critical'
                                },
                                {
                                    icon: '⚡',
                                    title: 'Crisis Management Plans',
                                    desc: 'Generate ISO-aligned Crisis Management Plans in minutes. AI synthesises your operating environment into structured, actionable crisis response frameworks with stakeholder maps.',
                                    color: 'from-violet-500/10 to-purple-500/10 border-violet-500/20',
                                    tag: 'Enterprise Ready'
                                },
                                {
                                    icon: '📋',
                                    title: 'HSSE Compliance Suite',
                                    desc: 'Automated health, safety, security & environmental audits aligned with ISO 45001, NEBOSH and industry standards. Track, report and resolve compliance gaps faster.',
                                    color: 'from-cyan-500/10 to-blue-500/10 border-cyan-500/20',
                                    tag: 'ISO Aligned'
                                },
                                {
                                    icon: '🎯',
                                    title: 'VCMC Simulation Platform',
                                    desc: 'Live, scenario-based crisis exercises with AI-controlled opposing force. Test your CMP in a realistic virtual environment and identify gaps before a real incident.',
                                    color: 'from-indigo-500/10 to-blue-500/10 border-indigo-500/20',
                                    tag: 'Simulation'
                                },
                                {
                                    icon: '🎓',
                                    title: 'Learning Management System',
                                    desc: 'Build bespoke training pathways, competency frameworks and certification programmes tailored to your industry. Track staff readiness across the full security lifecycle.',
                                    color: 'from-blue-500/10 to-sky-500/10 border-blue-500/20',
                                    tag: 'Staff Development'
                                },
                                {
                                    icon: '📊',
                                    title: 'Advanced Reporting Engine',
                                    desc: 'Generate PMESII-PT intelligence reports, executive briefings, composite country risk assessments and operational analytics — all PDF-exportable with a single click.',
                                    color: 'from-violet-500/10 to-indigo-500/10 border-violet-500/20',
                                    tag: 'Decision Support'
                                },
                            ].map(card => (
                                <div key={card.title} className={`rounded-xl bg-gradient-to-br ${card.color} border p-6 hover:scale-[1.02] transition-transform duration-200`}>
                                    <div className="text-3xl mb-4">{card.icon}</div>
                                    <div className="text-[10px] font-bold text-blue-400 tracking-widest uppercase mb-2">{card.tag}</div>
                                    <h3 className="font-bold text-white text-base mb-2">{card.title}</h3>
                                    <p className="text-slate-400 text-sm leading-relaxed">{card.desc}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {[
                                {
                                    icon: '🏛️',
                                    title: 'Multi-Client Workspace Management',
                                    desc: 'Create and manage up to 10 independent client programmes from one command dashboard. Activate, suspend and monitor all client intelligence operations in real time.',
                                    color: 'from-amber-500/10 to-orange-500/10 border-amber-500/20',
                                    tag: 'Agency Core'
                                },
                                {
                                    icon: '🪟',
                                    title: 'Client Portal Access',
                                    desc: 'Each client gets a branded portal to view their intelligence reports, interact with findings and communicate directly with your agency team — all within IntelOptics.',
                                    color: 'from-orange-500/10 to-red-500/10 border-orange-500/20',
                                    tag: 'Client Experience'
                                },
                                {
                                    icon: '🎨',
                                    title: 'White-Label Branding',
                                    desc: 'All PDF reports, executive briefs and deliverables can carry your agency logo and brand identity — not IntelOptics. Present a seamless professional service to clients.',
                                    color: 'from-yellow-500/10 to-amber-500/10 border-yellow-500/20',
                                    tag: 'Your Brand'
                                },
                                {
                                    icon: '💰',
                                    title: 'Agency Billing Infrastructure',
                                    desc: 'Manage client subscriptions, bill for managed security services, and track revenue per client. IntelOptics provides the platform — you keep the billing relationship.',
                                    color: 'from-amber-500/10 to-yellow-500/10 border-amber-500/20',
                                    tag: 'Revenue Engine'
                                },
                                {
                                    icon: '⚡',
                                    title: 'Priority AI Pipeline',
                                    desc: 'Agency accounts receive fast-lane processing for all AI analysis and report generation. No queues, no caps — essential for high-volume consultancy operations.',
                                    color: 'from-orange-500/10 to-amber-500/10 border-orange-500/20',
                                    tag: 'Performance'
                                },
                                {
                                    icon: '📈',
                                    title: 'Revenue Sharing Framework',
                                    desc: 'Charge your clients for the managed intelligence services you deliver through IntelOptics. Build a profitable recurring revenue stream on top of your subscription.',
                                    color: 'from-red-500/10 to-orange-500/10 border-red-500/20',
                                    tag: 'Profit Maximisation'
                                },
                            ].map(card => (
                                <div key={card.title} className={`rounded-xl bg-gradient-to-br ${card.color} border p-6 hover:scale-[1.02] transition-transform duration-200`}>
                                    <div className="text-3xl mb-4">{card.icon}</div>
                                    <div className="text-[10px] font-bold text-amber-400 tracking-widest uppercase mb-2">{card.tag}</div>
                                    <h3 className="font-bold text-white text-base mb-2">{card.title}</h3>
                                    <p className="text-slate-400 text-sm leading-relaxed">{card.desc}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ═══ COMPARISON TABLE ═══ */}
                <div className="mt-20">
                    <div className="text-center mb-10">
                        <h2 className="text-3xl font-black text-white mb-3">Full Feature Comparison</h2>
                        <p className="text-slate-400">See exactly what's included in each tier</p>
                    </div>

                    <div className="rounded-2xl border border-white/10 overflow-hidden">
                        {/* Table header */}
                        <div className="grid grid-cols-4 bg-white/5 border-b border-white/10">
                            <div className="p-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Feature</div>
                            <div className="p-4 text-center">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Individual</div>
                                <div className="text-sm font-black text-white mt-1">$99/mo</div>
                            </div>
                            <div className="p-4 text-center bg-blue-500/5 border-x border-blue-500/15">
                                <div className="text-xs font-bold text-blue-400 uppercase tracking-widest">Business</div>
                                <div className="text-sm font-black text-white mt-1">$349/mo</div>
                            </div>
                            <div className="p-4 text-center bg-amber-500/5">
                                <div className="text-xs font-bold text-amber-400 uppercase tracking-widest">Agency</div>
                                <div className="inline-flex items-center gap-1 mt-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] font-black text-slate-400 uppercase tracking-wide">
                                    <span className="w-1 h-1 rounded-full bg-amber-400 animate-pulse" />
                                    Coming Soon
                                </div>
                            </div>
                        </div>

                        {/* Rows */}
                        {COMPARISON.map((row, i) => (
                            <div
                                key={row.feature}
                                className={`grid grid-cols-4 border-b border-white/5 ${i % 2 === 0 ? 'bg-white/[0.02]' : ''}`}
                            >
                                <div className="p-4 text-sm text-slate-300 font-medium flex items-center">{row.feature}</div>
                                <div className="p-4 flex items-center justify-center">
                                    <FeatureCheck value={row.individual} />
                                </div>
                                <div className="p-4 flex items-center justify-center bg-blue-500/5 border-x border-blue-500/10">
                                    <FeatureCheck value={row.business} />
                                </div>
                                <div className="p-4 flex items-center justify-center bg-amber-500/5">
                                    <FeatureCheck value={row.agency} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ═══ BOTTOM CTA ═══ */}
                <div className="mt-20 relative rounded-2xl overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-violet-600/15 to-amber-600/10" />
                    <div className="absolute inset-0 border border-white/10 rounded-2xl" />
                    <div className="relative p-12 text-center">
                        <div className="text-4xl mb-4">🚀</div>
                        <h2 className="text-3xl font-black text-white mb-4">Ready to Elevate Your Operations?</h2>
                        <p className="text-slate-400 max-w-lg mx-auto mb-10">
                            Join security professionals and consultancies who trust IntelOptics to power 
                            their intelligence operations. Upgrade instantly — no lock-in, no contracts.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => handleUpgrade('business')}
                                className="px-10 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white font-bold tracking-wide transition-all shadow-xl shadow-blue-900/30 hover:-translate-y-0.5"
                            >
                                Upgrade to Business — ${prices.business[billingCycle]}/mo
                            </button>
                            {/* Agency CTA — Coming Soon */}
                            <div className="px-10 py-4 rounded-xl border-2 border-dashed border-white/10 bg-white/[0.02] flex items-center justify-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                                <span className="text-slate-400 font-bold text-sm">Agency Platform — Coming Soon</span>
                            </div>
                        </div>
                        <p className="text-slate-500 text-sm mt-6">
                            Questions? <a href="mailto:contact@eprims.com" className="text-blue-400 hover:text-blue-300 underline transition-colors">Contact our team</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpgradeBenefitsPage;
